<template>
  <section class="completions-section panel">
    <details class="completions-details">
      <summary class="completions-summary">
        Прохождения ({{ completionsCount }})
      </summary>

      <form v-if="canCreate" class="completion-form" @submit.prevent="submitCompletion">
        <label class="completion-date-label">
          Дата
          <input
            v-model="completedAt"
            type="date"
            class="completion-date-input"
            :max="today"
            :disabled="submitting"
            required
          />
        </label>

        <VehiclePicker
          v-model="vehicleId"
          :disabled="submitting"
          @quick-create="onQuickCreateVehicle"
        />

        <button type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit">
          {{ submitting ? 'Сохранение...' : 'Отметить прохождение' }}
        </button>
        <p v-if="formError" class="error-message">{{ formError }}</p>
      </form>

      <p v-if="!completions.length" class="empty-state">
        Пока никто не отмечал прохождение
      </p>

      <ul v-else class="completion-list">
        <li v-for="completion in completions" :key="completion.id" class="completion-item">
          <div class="completion-item-info">
            <AuthorLink :author="completion.author" size="sm" class="completion-author" />
            <span class="completion-date">{{ formatDate(completion.completed_at) }}</span>
            <span v-if="completion.vehicle" class="completion-vehicle">
              {{ completion.vehicle.make }}
            </span>
          </div>
          <button
            v-if="canDelete(completion)"
            type="button"
            class="completion-delete"
            :disabled="deletingId === completion.id"
            aria-label="Удалить прохождение"
            @click="$emit('delete', completion)"
          >
            <svg
              class="completion-delete-icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden="true"
            >
              <path
                d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
              />
            </svg>
          </button>
        </li>
      </ul>
    </details>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import AuthorLink from './AuthorLink.vue'
import VehiclePicker from './VehiclePicker.vue'

const props = defineProps({
  completions: {
    type: Array,
    default: () => [],
  },
  completionsCount: {
    type: Number,
    default: 0,
  },
  canCreate: {
    type: Boolean,
    default: true,
  },
  canDelete: {
    type: Function,
    required: true,
  },
  deletingId: {
    type: [Number, null],
    default: null,
  },
  submitting: {
    type: Boolean,
    default: false,
  },
  formError: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['create', 'delete'])

const today = new Date().toISOString().slice(0, 10)
const completedAt = ref(today)
const vehicleId = ref('')
const quickCreateVehicle = ref(null)

const canSubmit = computed(() => {
  if (quickCreateVehicle.value?.make?.trim()) return true
  return Boolean(vehicleId.value)
})

function formatDate(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'long' }).format(new Date(value))
}

function onQuickCreateVehicle(payload) {
  quickCreateVehicle.value = payload
  if (payload) vehicleId.value = ''
}

function submitCompletion() {
  emit('create', {
    completedAt: completedAt.value,
    vehicleId: vehicleId.value ? Number(vehicleId.value) : null,
    vehicle: quickCreateVehicle.value?.make?.trim()
      ? {
          make: quickCreateVehicle.value.make,
          equipment: quickCreateVehicle.value.equipment,
        }
      : null,
    photos: quickCreateVehicle.value?.photos || [],
  })
}
</script>

<style scoped>
.completions-section {
  margin-top: 1.5rem;
}

.completions-details {
  margin: 0;
}

.completions-summary {
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
  list-style: none;
}

.completions-summary::-webkit-details-marker {
  display: none;
}

.completions-summary::before {
  content: '▸ ';
  display: inline-block;
  transition: transform 0.15s ease;
}

.completions-details[open] .completions-summary::before {
  transform: rotate(90deg);
}

.completion-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.completion-date-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
}

.completion-date-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.completion-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.completion-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.completion-item-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
}

.completion-author {
  min-width: 0;
}

.completion-date,
.completion-vehicle {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.3;
}

.completion-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.25rem;
  background: #fff;
  color: #94a3b8;
  cursor: pointer;
  line-height: 0;
}

.completion-delete:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.completion-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.completion-delete-icon {
  display: block;
  fill: currentColor;
}
</style>
